import { useState, forwardRef } from "react";
import { X } from "lucide-react";
import styles from "./TextInput.module.css";

const TextInput = forwardRef(
  (
    {
      value = "",
      onChange,
      onBlur,
      onFocus,
      name,
      id,
      placeholder = "",
      type = "text",
      leadIcon,
      trailIcon,
      prefixText,
      suffixText,
      clearable = false,
      maxLength,
      showCharCount = false,
      disabled = false,
      readOnly = false,
      error = false,
      autoFocus = false,
      className = "",
      inputClassName = "",
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    const handleClear = (e) => {
      e.stopPropagation();
      if (onChange) {
        const syntheticEvent = {
          target: { name, value: "" },
        };
        onChange(syntheticEvent);
      }
    };

    const strValue = value !== null && value !== undefined ? String(value) : "";
    const hasValue = strValue.length > 0;

    const wrapperClass = [
      styles.inputWrapper,
      isFocused ? styles.focused : "",
      error ? styles.hasError : "",
      disabled ? styles.disabled : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={wrapperClass}>
        {prefixText && <span className={styles.prefixBadge}>{prefixText}</span>}

        {leadIcon && <span className={styles.leadIconSlot}>{leadIcon}</span>}

        <input
          ref={ref}
          id={id || name}
          name={name}
          type={type}
          value={strValue}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          autoFocus={autoFocus}
          className={`${styles.input} ${leadIcon ? styles.hasLeadIcon : ""} ${trailIcon ? styles.hasTrailIcon : ""} ${inputClassName}`.trim()}
          {...props}
        />

        {clearable && hasValue && !disabled && !readOnly && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            tabIndex={-1}
            aria-label="Clear text"
          >
            <X size={14} />
          </button>
        )}

        {showCharCount && maxLength && (
          <span className={styles.charCounter}>
            {strValue.length}/{maxLength}
          </span>
        )}

        {trailIcon && <span className={styles.trailIconSlot}>{trailIcon}</span>}

        {suffixText && <span className={styles.suffixBadge}>{suffixText}</span>}
      </div>
    );
  }
);

TextInput.displayName = "TextInput";

export default TextInput;
